import {
  Permission,
  WithPermissions,
  hasPermissionIn,
} from "src/types/Permission";

/**
 * Права на узел дерева.
 *
 * Проверка та же, что и во всём приложении: `hasPermissionIn` считает
 * отсутствие списка прав разрешением. Отсутствие поля означает, что сервер его
 * не присылает, а не что сущность защищена, — придумывать по этому поводу
 * запрет значило бы блокировать работу на ровном месте.
 *
 * Незагруженные данные — отдельный случай, и он выражается не здесь, а тем,
 * какие именно данные передают в эти функции: пока деталей объекта нет,
 * права берутся из `summaryActions`.
 */
export type NodeActions = {
  update: boolean;
  delete: boolean;
};

export const deniedActions: NodeActions = { update: false, delete: false };

/**
 * Собственных ограничений нет — узел свободен настолько, насколько свободен
 * его владелец. Так устроены группы и значения справочника: своего списка
 * прав у них не бывает.
 */
export const allowedActions: NodeActions = { update: true, delete: true };

/** Права по списку permissions самой сущности. */
export const actionsOf = (item: WithPermissions): NodeActions => ({
  update: hasPermissionIn(item, Permission.objUpdate),
  delete: hasPermissionIn(item, Permission.objDelete),
});

/**
 * Собственные права атрибута.
 *
 * ATTRIBUTE_CREATE и ATTRIBUTE_DELETE больше не настраиваются (LPLM-4852),
 * поэтому сервер не присылает ATTRIBUTE_DELETE даже пользователю, который
 * может менять структуру объекта. Удаление атрибута ограничивает владелец,
 * а собственный список атрибута отвечает только за его изменение.
 */
export const attributeActionsOf = (item: WithPermissions): NodeActions => ({
  update: hasPermissionIn(item, Permission.attrUpdate),
  delete: true,
});

/**
 * Права до загрузки деталей: по флагам из каталога.
 *
 * Флага удаления сервер может не присылать - тогда он приходит выключенным, и
 * удаление объекта откроется после загрузки его данных.
 */
export const summaryActions = (flags: {
  canUpdate: boolean;
  canDelete: boolean;
}): NodeActions => ({
  update: flags.canUpdate,
  delete: flags.canDelete,
});

/**
 * Права, которые объект передаёт вложенным узлам.
 *
 * Удалить атрибут или группу - это изменить объект, а не удалить его: своего
 * права на удаление у них нет и на сервере. Поэтому для потомков право
 * удаления берётся из права изменения объекта.
 */
export const childActionsOf = (objectActions: NodeActions): NodeActions => ({
  update: objectActions.update,
  delete: objectActions.update,
});

/** Дочерний узел не может быть свободнее владельца. */
export const mergeActions = (
  owner: NodeActions,
  own: NodeActions,
): NodeActions => ({
  update: owner.update && own.update,
  delete: owner.delete && own.delete,
});
